/*
 * Author: zoesvision.com
 * Purpose: Build and manage accessible navigation for nested Z Tabs Theme Blocks.
 */

class ZTabsBlock extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.navigation = this.querySelector('[data-z-tabs-navigation]');
    this.panelsContainer = this.querySelector('[data-z-tabs-panels]');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

    if (!this.navigation || !this.panelsContainer) return;

    this.navigation.addEventListener('click', this.onClick, { signal: this.abortController.signal });
    this.navigation.addEventListener('keydown', this.onKeydown, { signal: this.abortController.signal });
    document.addEventListener('shopify:block:select', this.onBlockSelect, {
      signal: this.abortController.signal
    });

    this.mutationObserver = new MutationObserver(this.onPanelsMutation);
    this.mutationObserver.observe(this.panelsContainer, {
      childList: true,
      subtree: true,
      characterData: true
    });

    this.refresh();
    this.resizeObserver = new ResizeObserver(() => this.updateIndicator());
    this.resizeObserver.observe(this.navigation);
    this.dataset.zTabsReady = 'true';
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.abortController = null;
    this.mutationObserver = null;
    this.navigation = null;
    this.panelsContainer = null;
    this.panels = [];
    this.triggers = [];
  }

  refresh() {
    const activeId = this.triggers?.find((trigger) => trigger.getAttribute('aria-selected') === 'true')?.dataset.zTabId;
    this.panels = [...this.panelsContainer.children].filter((panel) => panel.matches('[data-z-tab-block]'));

    const triggers = this.panels.map((panel, index) => {
      const blockId = panel.dataset.zTabId;
      const sourceTitle = panel.querySelector('[data-z-tab-title-source]')?.textContent.trim();
      const title = sourceTitle || `Tab ${index + 1}`;
      const trigger = document.createElement('button');

      trigger.id = `z-tab-trigger-${this.dataset.zSectionId}-${blockId}`;
      trigger.className = 'z-tabs__trigger';
      trigger.type = 'button';
      trigger.role = 'tab';
      trigger.dataset.zTabTrigger = '';
      trigger.dataset.zTabId = blockId;
      trigger.textContent = title;
      trigger.setAttribute('aria-controls', panel.id);
      panel.setAttribute('aria-labelledby', trigger.id);

      const fallbackTitle = panel.querySelector('[data-z-tab-fallback-title]');
      if (fallbackTitle && fallbackTitle.textContent.trim() !== title) fallbackTitle.textContent = title;

      return trigger;
    });

    this.navigation.replaceChildren(...triggers);
    this.triggers = triggers;

    const activeIndex = Math.max(0, this.triggers.findIndex((trigger) => trigger.dataset.zTabId === activeId));
    this.activate(activeIndex, { announce: false, scroll: false });
  }

  activate(index, { focus = false, announce = true, scroll = true } = {}) {
    const trigger = this.triggers[index];
    if (!trigger) return;

    const blockId = trigger.dataset.zTabId;
    this.triggers.forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.setAttribute('aria-selected', String(active));
      item.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const active = panel.dataset.zTabId === blockId;
      panel.hidden = !active;
      panel.setAttribute('aria-hidden', String(!active));
      panel.inert = !active;
    });

    this.updateIndicator();

    if (focus) trigger.focus();
    if (scroll) {
      trigger.scrollIntoView({
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }

    if (announce) {
      this.dispatchEvent(new CustomEvent('z:tabs:change', {
        bubbles: true,
        detail: { index, blockId }
      }));
    }
  }

  updateIndicator() {
    const activeTrigger = this.triggers.find((trigger) => trigger.getAttribute('aria-selected') === 'true');
    if (!this.navigation || !activeTrigger) return;

    this.navigation.style.setProperty('--z-tabs-indicator-x', `${activeTrigger.offsetLeft}px`);
    this.navigation.style.setProperty('--z-tabs-indicator-width', `${activeTrigger.offsetWidth}px`);
    if (this.navigation.dataset.zIndicatorReady !== 'true') {
      requestAnimationFrame(() => {
        if (this.navigation?.isConnected) this.navigation.dataset.zIndicatorReady = 'true';
      });
    }
  }

  onClick = (event) => {
    const trigger = event.target.closest('[data-z-tab-trigger]');
    if (!trigger || !this.navigation.contains(trigger)) return;

    this.activate(this.triggers.indexOf(trigger));
  };

  onKeydown = (event) => {
    const trigger = event.target.closest('[data-z-tab-trigger]');
    if (!trigger || !this.navigation.contains(trigger)) return;

    const index = this.triggers.indexOf(trigger);
    const lastIndex = this.triggers.length - 1;
    let targetIndex = null;

    if (event.key === 'ArrowRight') targetIndex = (index + 1) % this.triggers.length;
    if (event.key === 'ArrowLeft') targetIndex = (index - 1 + this.triggers.length) % this.triggers.length;
    if (event.key === 'Home') targetIndex = 0;
    if (event.key === 'End') targetIndex = lastIndex;
    if (targetIndex === null) return;

    event.preventDefault();
    this.activate(targetIndex, { focus: true });
  };

  onBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const panel = event.target.closest('[data-z-tab-block]');
    if (!panel || panel.parentElement !== this.panelsContainer) return;

    const index = this.panels.indexOf(panel);
    if (index >= 0) this.activate(index);
  };

  onPanelsMutation = () => {
    if (this.refreshQueued) return;

    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      this.refresh();
    });
  };
}

if (!customElements.get('z-tabs-block')) {
  customElements.define('z-tabs-block', ZTabsBlock);
}
